import React, { useRef, useState, useCallback } from 'react';
import { X, Download, Upload, Users, Building2, DollarSign } from 'lucide-react';
import { toast } from 'react-hot-toast';
import contactsService from '../../services/crm/contactsService';
import companiesService from '../../services/crm/companiesService';
import dealsService from '../../services/crm/dealsService';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onImportComplete?: () => void;
}

type ImportType = 'contacts' | 'companies' | 'deals';

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  onImportComplete
}) => {
  const [activeTab, setActiveTab] = useState<ImportType>('contacts');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = useCallback((type: ImportType) => {
    let headers: string[];
    let sampleRow: string[];
    let filename: string;

    switch (type) {
      case 'contacts':
        headers = ['first_name', 'last_name', 'email', 'phone', 'job_title', 'company_id'];
        sampleRow = ['John', 'Doe', 'john.doe@example.com', '+1234567890', 'CEO', ''];
        filename = 'contacts_template.csv';
        break;
      case 'companies':
        headers = ['name', 'domain', 'industry', 'size', 'linkedin_url'];
        sampleRow = ['Acme Corp', 'acme.com', 'Technology', '50-100', 'https://linkedin.com/company/acme'];
        filename = 'companies_template.csv';
        break;
      case 'deals':
        headers = ['title', 'amount', 'stage_id', 'company_id', 'contact_id', 'close_date'];
        sampleRow = ['Q1 Enterprise Deal', '50000', '', '', '', '2025-03-31'];
        filename = 'deals_template.csv';
        break;
    }

    const csv = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  }, []);

  const parseCsv = useCallback((text: string) => {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
    if (!lines.length) return [];

    const rows: Record<string, string>[] = [];
    const headers = lines[0].split(',').map(h => h.trim());

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(',').map(v => v.trim());
      const row: Record<string, string> = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      rows.push(row);
    }

    return rows;
  }, []);

  const handleImport = useCallback(async (file: File) => {
    if (!organizationId) {
      toast.error('Organization not selected');
      return;
    }

    setImporting(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);

      if (rows.length === 0) {
        toast.error('No data found in CSV');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const row of rows) {
        try {
          const payload = { ...row, organization_id: organizationId };

          switch (activeTab) {
            case 'contacts':
              await contactsService.create(payload as any);
              break;
            case 'companies':
              await companiesService.create(payload as any);
              break;
            case 'deals':
              await dealsService.create(payload as any);
              break;
          }
          successCount++;
        } catch (err) {
          errorCount++;
          console.error('Import row error:', err);
        }
      }

      toast.success(`Imported ${successCount} ${activeTab}${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      onImportComplete?.();
      onClose();
    } catch (err) {
      toast.error('Failed to import CSV');
      console.error(err);
    } finally {
      setImporting(false);
    }
  }, [activeTab, organizationId, parseCsv, onImportComplete, onClose]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImport(file);
    }
    e.target.value = '';
  }, [handleImport]);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  if (!isOpen) return null;

  const tabs = [
    { id: 'contacts' as ImportType, label: 'Contacts', icon: Users },
    { id: 'companies' as ImportType, label: 'Companies', icon: Building2 },
    { id: 'deals' as ImportType, label: 'Deals', icon: DollarSign }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Import Historical Data</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6 rounded-lg bg-blue-50 p-4">
            <h3 className="mb-2 text-sm font-semibold text-blue-900">How to import {activeTab}</h3>
            <ol className="space-y-1 text-sm text-blue-700">
              <li>1. Download the CSV template below</li>
              <li>2. Fill in your data (keep the header row)</li>
              <li>3. Upload the completed CSV file</li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => downloadTemplate(activeTab)}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Download CSV Template
            </button>
            <button
              onClick={triggerFileInput}
              disabled={importing}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {importing ? 'Importing...' : `Upload ${activeTab} CSV`}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileChange}
          />

          {/* Field Reference */}
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="mb-2 text-xs font-semibold uppercase text-gray-600">CSV Fields Reference</h4>
            <div className="text-xs text-gray-600">
              {activeTab === 'contacts' && (
                <ul className="space-y-1">
                  <li><strong>first_name:</strong> First name</li>
                  <li><strong>last_name:</strong> Last name</li>
                  <li><strong>email:</strong> Email address</li>
                  <li><strong>phone:</strong> Phone number (optional)</li>
                  <li><strong>job_title:</strong> Job title (optional)</li>
                  <li><strong>company_id:</strong> Company UUID (optional, leave blank if unknown)</li>
                </ul>
              )}
              {activeTab === 'companies' && (
                <ul className="space-y-1">
                  <li><strong>name:</strong> Company name (required)</li>
                  <li><strong>domain:</strong> Website domain (optional)</li>
                  <li><strong>industry:</strong> Industry (optional)</li>
                  <li><strong>size:</strong> Employee count (optional)</li>
                  <li><strong>linkedin_url:</strong> LinkedIn URL (optional)</li>
                </ul>
              )}
              {activeTab === 'deals' && (
                <ul className="space-y-1">
                  <li><strong>title:</strong> Deal name (required)</li>
                  <li><strong>amount:</strong> Deal value in dollars</li>
                  <li><strong>stage_id:</strong> Deal stage UUID (required)</li>
                  <li><strong>company_id:</strong> Company UUID (optional)</li>
                  <li><strong>contact_id:</strong> Contact UUID (optional)</li>
                  <li><strong>close_date:</strong> Expected close date (YYYY-MM-DD)</li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
